/**
 * Generate the <Link rel="preload" as="image" ... /> for the hero images. (mobile & desktop)
 */
import type { MetaDescriptor } from 'react-router';
import { imageConfig } from '@modules/images';
import type { ImageLoader } from '@archipro-design/aria';

interface PreloadWidthOptions {
    desktopWidth?: number;
    mobileWidth?: number;
}

// Default widths optimized for typical device DPR
const DEFAULT_DESKTOP_WIDTH = 1920;
const DEFAULT_MOBILE_WIDTH = 828; // 414px viewport × 2 DPR

/**
 * Generate the <Link rel="preload" as="image" ... /> for the hero images. (mobile & desktop)
 * @param imageDesktop Desktop hero image URL
 * @param imageMobile Mobile hero image URL
 * @param imageSizes Sizes attribute for responsive image selection
 * @param widths Optional overrides for the desktop/mobile preload widths
 */
export const getPreloadImageLink = (
    imageDesktop?: string,
    imageMobile?: string,
    imageSizes?: string,
    widths?: PreloadWidthOptions,
): MetaDescriptor[] => {
    const defaultLinksAttr = {
        rel: 'preload',
        as: 'image',
        tagName: 'link',
        fetchpriority: 'high',
    };
    const dynamicImageLinks: MetaDescriptor[] = [];
    const loader = imageConfig.loader;

    if (imageDesktop) {
        const desktopWidth = widths?.desktopWidth ?? DEFAULT_DESKTOP_WIDTH;
        dynamicImageLinks.push({
            ...defaultLinksAttr,
            href: loader({
                src: imageDesktop,
                width: desktopWidth,
            }),
            media: '(min-width: 1024px)',
            // Filter srcset to max 2x the target width for reasonable DPR coverage
            imageSrcSet: generateImageSrcSet(
                loader,
                imageDesktop,
                desktopWidth * 2,
            ),
            imageSizes: imageSizes || '100vw',
        });
    }

    if (imageMobile) {
        const mobileWidth = widths?.mobileWidth ?? DEFAULT_MOBILE_WIDTH;
        dynamicImageLinks.push({
            ...defaultLinksAttr,
            href: loader({
                src: imageMobile,
                width: mobileWidth,
            }),
            media: '(max-width: 768px)',
            // Cap srcset to the specified mobile width to match rendered dimensions
            imageSrcSet: generateImageSrcSet(loader, imageMobile, mobileWidth),
            imageSizes: imageSizes || '100vw',
        });
    }

    return dynamicImageLinks;
};

/**
 * Generate srcset attribute with optional maxWidth filter
 * @param loader Image loader function
 * @param src Image source URL
 * @param maxWidth Maximum width to include in srcset (filters out larger sizes)
 */
const generateImageSrcSet = (
    loader: ImageLoader,
    src: string,
    maxWidth?: number,
): string => {
    const deviceSizes = imageConfig.deviceSizes;
    const filteredSizes = maxWidth
        ? deviceSizes.filter((w) => w <= maxWidth)
        : deviceSizes;

    return filteredSizes
        .map((w) => {
            return `${loader({
                src: src,
                width: w,
            })} ${w}w`;
        })
        .join(',');
};
